/**
 * BaseToolCallCard — shared wrapper for all tool call renderers.
 *
 * Handles the complete state machine (draft → in-flight → done/denied/failed + superseded),
 * status badge, action buttons, allowlist toggle, and overlays.
 *
 * Modules provide only the card content via children prop.
 *
 * Invariants enforced:
 * - INV-1: Always shows status badge
 * - INV-2: Failed result → "Failed" (red), never "Applied"
 * - INV-3: Superseded = opacity-50, no action buttons
 * - INV-4: Done = green badge, no action buttons
 * - INV-5: Denied = red badge, no action buttons
 * - INV-6: Draft = action buttons visible
 */

import { useState, type ReactNode } from "react";
import type { JSX } from "react";
import { Icon } from "../../components/ui";
import type { IconName } from "../../components/ui/Icon";
import { AllowlistDropdown } from "../../components/agent/AgentPanelBlocks";
import type { AllowlistScope, AllowlistState } from "../../runtime/contracts";

// ─── State machine ──────────────────────────────────────────────────

export type ToolCallState =
  | "draft"
  | "in-flight"
  | "done"
  | "failed"
  | "denied"
  | "superseded";

// eslint-disable-next-line react-refresh/only-export-components
export function resolveToolCallState(
  status: "pending" | "approved" | "denied",
  superseded: boolean,
  inFlight: boolean,
  toolResult?: { result: unknown },
): ToolCallState {
  if (superseded) return "superseded";
  if (status === "denied") return "denied";
  if (status === "approved") {
    // Check if result contains an error
    if (toolResult) {
      const r = toolResult.result;
      if (r != null && typeof r === "object" && "error" in (r as Record<string, unknown>)) {
        return "failed";
      }
      if (typeof r === "string" && r.startsWith("Error")) {
        return "failed";
      }
    }
    return "done";
  }
  if (inFlight) return "in-flight";
  return "draft";
}

// ─── Badge config ───────────────────────────────────────────────────

interface BadgeConfig {
  readonly text: string;
  readonly bg: string;
  readonly border: string;
  readonly textClass: string;
}

const STATE_BADGES: Record<ToolCallState, BadgeConfig> = {
  draft: {
    text: "Draft",
    bg: "bg-[var(--color-agent-status-draft-bg)]",
    border: "border-[var(--color-agent-status-draft-border)]",
    textClass: "text-[var(--color-agent-status-draft-text)]",
  },
  "in-flight": {
    text: "Applying...",
    bg: "bg-[var(--color-agent-status-draft-bg)]",
    border: "border-[var(--color-agent-status-draft-border)]",
    textClass: "text-[var(--color-agent-status-draft-text)]",
  },
  done: {
    text: "Applied",
    bg: "bg-[var(--color-agent-status-done-bg)]",
    border: "border-[var(--color-agent-status-done-border)]",
    textClass: "text-[var(--color-agent-status-done-text)]",
  },
  failed: {
    text: "Failed",
    bg: "bg-[var(--color-agent-status-failed-bg)]",
    border: "border-[var(--color-agent-status-failed-border)]",
    textClass: "text-[var(--color-agent-status-failed-text)]",
  },
  denied: {
    text: "Denied",
    bg: "bg-[var(--color-agent-status-failed-bg)]",
    border: "border-[var(--color-agent-status-failed-border)]",
    textClass: "text-[var(--color-agent-status-failed-text)]",
  },
  superseded: {
    text: "Superseded",
    bg: "bg-[var(--color-agent-status-failed-bg)]",
    border: "border-[var(--color-agent-status-failed-border)]",
    textClass: "text-[var(--color-agent-status-failed-text)]",
  },
};

// ─── Variant colors ─────────────────────────────────────────────────

interface VariantColors {
  readonly border: string;
  readonly bg: string;
  readonly iconColor: string;
  readonly primaryBg: string;
  readonly primaryHover: string;
}

const VARIANT_COLORS: Record<string, VariantColors> = {
  amber: {
    border: "border-[var(--color-agent-tool-amber-border)]",
    bg: "bg-[var(--color-agent-tool-amber-bg)]",
    iconColor: "text-[var(--color-agent-tool-amber-text)]",
    primaryBg: "bg-[var(--color-agent-tool-amber-primary)]",
    primaryHover: "hover:bg-[var(--color-agent-tool-amber-primary-hover)]",
  },
  sky: {
    border: "border-[var(--color-agent-tool-sky-border)]",
    bg: "bg-[var(--color-agent-tool-sky-bg)]",
    iconColor: "text-[var(--color-agent-tool-sky-text)]",
    primaryBg: "bg-[var(--color-agent-tool-sky-primary)]",
    primaryHover: "hover:bg-[var(--color-agent-tool-sky-primary-hover)]",
  },
  rose: {
    border: "border-[var(--color-agent-tool-rose-border)]",
    bg: "bg-[var(--color-agent-tool-rose-bg)]",
    iconColor: "text-[var(--color-agent-tool-rose-text)]",
    primaryBg: "bg-[var(--color-agent-tool-rose-primary)]",
    primaryHover: "hover:bg-[var(--color-agent-tool-rose-primary-hover)]",
  },
  teal: {
    border: "border-[var(--color-agent-tool-teal-border)]",
    bg: "bg-[var(--color-agent-tool-teal-bg)]",
    iconColor: "text-[var(--color-agent-tool-teal-text)]",
    primaryBg: "bg-[var(--color-agent-tool-teal-primary)]",
    primaryHover: "hover:bg-[var(--color-agent-tool-teal-primary-hover)]",
  },
  purple: {
    border: "border-[var(--color-agent-tool-purple-border)]",
    bg: "bg-[var(--color-agent-tool-purple-bg)]",
    iconColor: "text-[var(--color-agent-tool-purple-text)]",
    primaryBg: "bg-[var(--color-agent-tool-purple-primary)]",
    primaryHover: "hover:bg-[var(--color-agent-tool-purple-primary-hover)]",
  },
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const DEFAULT_COLORS: VariantColors = VARIANT_COLORS.amber!;

// ─── Props ──────────────────────────────────────────────────────────

export interface BaseToolCallCardProps {
  /** Icon for the card header */
  readonly icon: IconName;
  /** Title text (e.g. "New Note", "Telegram to Chat") */
  readonly title: string;
  /** Color variant */
  readonly variant: "amber" | "sky" | "rose" | "teal" | "purple";

  /** Tool call status from payload */
  readonly status: "pending" | "approved" | "denied";
  /** Tool result from payload (checked for errors) */
  readonly toolResult?: { readonly id: string; readonly result: unknown };
  /** Whether this card was superseded by a newer tool call */
  readonly superseded?: boolean;
  /** Whether this action is in the allowlist */
  readonly isAllowlisted?: AllowlistState;

  /** Module-specific content */
  readonly children: ReactNode;

  /** Extra content rendered in the header row (between title and badge) */
  readonly headerExtra?: ReactNode;

  /** Primary action button label (default: "Apply") */
  readonly primaryLabel?: string;
  /** Primary action button icon (default: "check") */
  readonly primaryIcon?: IconName;
  /** Label when done (default: "Applied") */
  readonly doneLabel?: string;

  /** Callbacks */
  readonly onApprove: () => Promise<void> | void;
  readonly onDeny?: () => Promise<void> | void;
  readonly onEdit?: () => void;
  readonly onAllowlistToggle?: (scope?: AllowlistScope) => void;

  /** Navigation after apply (clickable done state) */
  readonly onNavigate?: () => void;

  /** When provided, completely replaces the default action bar in pending state */
  readonly customActions?: ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────

export function BaseToolCallCard({
  icon,
  title,
  variant,
  status,
  toolResult,
  superseded = false,
  isAllowlisted = false,
  children,
  headerExtra,
  primaryLabel = "Apply",
  primaryIcon = "check",
  doneLabel = "Applied",
  onApprove,
  onDeny,
  onEdit,
  onAllowlistToggle,
  onNavigate,
  customActions,
}: BaseToolCallCardProps): JSX.Element {
  const [inFlight, setInFlight] = useState(false);
  const [localDone, setLocalDone] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const state = resolveToolCallState(
    localDone ? "approved" : status,
    superseded,
    inFlight,
    toolResult,
  );

  const colors = (VARIANT_COLORS)[variant] ?? DEFAULT_COLORS;
  const badge = { ...STATE_BADGES[state] };

  // Override badge text for custom done labels
  if (state === "done" && doneLabel !== "Applied") {
    badge.text = doneLabel;
  }
  if (state === "in-flight" && primaryLabel) {
    badge.text = `${primaryLabel}ing...`;
  }

  const isPending = state === "draft";
  const isTerminal = state === "done" || state === "denied" || state === "failed";

  // Deny had the same defect approve did: `void onDeny()` dropped the
  // rejection as an unhandled promise, so the most likely refusal — the
  // binding is no longer pending — produced no visible outcome at all and the
  // button read as broken. @tested-by: tst_fe_agent_022
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDeny = async () => {
    if (!onDeny) return;
    setInFlight(true);
    setApproveError(null);
    try {
      await onDeny();
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : String(err));
    } finally {
      setInFlight(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handlePrimary = async () => {
    setInFlight(true);
    setApproveError(null);
    try {
      await onApprove();
      setLocalDone(true);
    } catch (err) {
      // The card MUST say why. Swallowing this left the only visible outcome
      // of a rejected approval as "nothing happened" — the button appeared
      // dead while the backend had answered (e.g. the approval binding was no
      // longer pending). The card stays in draft so a retry is still possible,
      // but the reason is now on screen.
      setApproveError(err instanceof Error ? err.message : String(err));
    } finally {
      setInFlight(false);
    }
  };

  return (
    <div
      className={`rounded-xl border ${colors.border} ${colors.bg} px-3.5 py-3 ${
        inFlight ? "opacity-70 pointer-events-none" : ""
      } ${superseded ? "opacity-50" : ""}`}
    >
      {/* ── Header: icon + title + [headerExtra] + badge ── */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={colors.iconColor}>
            <Icon name={icon} size={14} />
          </span>
          <span className="truncate text-[13px] font-semibold text-agent-text-heading">
            {title}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerExtra}
          <span
            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${badge.bg} ${badge.border} ${badge.textClass}`}
          >
            {badge.text}
          </span>
        </div>
      </div>

      {/* ── Content (module-specific) ── */}
      {/* @tested-by: tst_fe_base_card_height_001
       * @invariant: INV-15 — the card's own body scrolls; the card does not
       * grow with it. A long email pushed the Approve/Deny row below the
       * viewport with no way to reach it, so an approval the agent was waiting
       * on became unreachable. The cap lives on the SHARED card so every
       * module inherits it, rather than each renderer remembering. */}
      <div className="max-h-80 overflow-y-auto" data-testid="tool-call-card-body">
        {children}
      </div>

      {/* ── Actions or post-action status ── */}
      {isPending && customActions ? (
        <div className="mt-3">{customActions}</div>
      ) : isPending ? (
        <div className="mt-3">
          {approveError !== null && (
            <p
              data-testid="tool-approve-error"
              className="mb-2 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-[12px] text-red-300"
            >
              {approveError}
            </p>
          )}
          <div className="flex items-center gap-2">
          {onAllowlistToggle && (
            <AllowlistDropdown
              isAllowlisted={isAllowlisted}
              onToggle={onAllowlistToggle}
            />
          )}
          <div className="flex-1" />
          {onDeny && (
            <button
              className="flex items-center gap-1 rounded-md border border-agent-border px-2.5 py-1.5 text-[12px] text-agent-text-muted"
              type="button"
              onClick={() => void handleDeny()}
            >
              Deny
            </button>
          )}
          {onEdit && (
            <button
              className="flex items-center gap-1 rounded-md border border-agent-border px-2.5 py-1.5 text-[12px] text-agent-text-muted"
              type="button"
              onClick={onEdit}
            >
              <Icon name="edit" size={12} />
              Edit
            </button>
          )}
          <button
            className={`flex items-center gap-1 rounded-md ${colors.primaryBg} ${colors.primaryHover} px-3 py-1.5 text-[12px] font-medium text-white`}
            type="button"
            onClick={() => void handlePrimary()}
          >
            <Icon name={primaryIcon} size={12} />
            {primaryLabel}
          </button>
          </div>
        </div>
      ) : isTerminal ? (
        <div
          className={`mt-3 flex items-center gap-2 text-[12px] ${onNavigate && state === "done" ? "cursor-pointer" : ""}`}
          onClick={onNavigate && state === "done" ? onNavigate : undefined}
        >
          {(state === "done" || state === "failed") &&
            isAllowlisted &&
            onAllowlistToggle && (
              <AllowlistDropdown
                isAllowlisted={isAllowlisted}
                onToggle={onAllowlistToggle}
              />
            )}
          <div className="flex-1" />
          <span
            className={
              state === "done"
                ? "text-[var(--color-agent-status-done-text)]"
                : "text-[var(--color-agent-status-failed-text)]"
            }
          >
            <Icon
              name={state === "done" ? "circle-check" : "circle-alert"}
              size={13}
            />
          </span>
          <span className="text-agent-text-muted">{badge.text}</span>
          {onNavigate && state === "done" && (
            <Icon name="chevron-right" size={14} className="text-agent-text-muted" />
          )}
        </div>
      ) : null}
    </div>
  );
}
